import { evaluate_onboarding_execution_compliance } from '@/services/onboarding_journey_service';
import { first_value, normalize_text } from '@/services/onboarding_data_helpers';

const OUTCOME_TONE_RULES = [
  { tone: 'success', words: ['compliant', 'qualified', 'pass', 'passed'] },
  { tone: 'warning', words: ['partial', 'partially', 'review', 'pending'] },
  { tone: 'danger', words: ['non-compliant', 'unqualified', 'fail', 'failed', 'blocked'] }
];

function tone_from_outcome(value = '') {
  const normalized = normalize_text(value);
  const matched_rule = OUTCOME_TONE_RULES.find((rule) => (
    rule.words.some((word) => normalized.includes(word))
  ));
  return matched_rule?.tone || 'secondary';
}

function normalize_rule(rule = {}, index = 0) {
  const evaluation = rule?.entity_evaluation || {};
  const outcome = first_value([
    evaluation?.outcome,
    rule?.outcome,
    rule?.status
  ], 'Not evaluated');
  const normalized_rule = {
    id: first_value([rule?.rule_id, rule?.id], `rule-${index}`),
    name: first_value([rule?.rule_name, rule?.name], 'Compliance rule'),
    outcome,
    message: first_value([evaluation?.eval_message, rule?.message, rule?.description], ''),
    last_evaluated: first_value([evaluation?.last_evaluated, rule?.last_evaluated], ''),
    tone: tone_from_outcome(outcome),
    has_evaluation: Boolean(evaluation?.outcome)
  };
  return normalized_rule;
}

function build_status_label(rules = []) {
  if (!rules.length) return 'Not configured';
  const status_rules = [
    { matches: () => rules.every((rule) => !rule.has_evaluation), label: 'Not evaluated' },
    { matches: () => rules.some((rule) => rule.tone === 'danger'), label: 'Action required' },
    { matches: () => rules.some((rule) => rule.tone === 'warning'), label: 'Review needed' }
  ];
  const matched_rule = status_rules.find((rule) => rule.matches());
  return matched_rule?.label || 'Compliant';
}

function normalize_actions(actions = []) {
  return (Array.isArray(actions) ? actions : []).map((action, index) => {
    const normalized_action = {
      id: `${first_value([action?.rule_id, action?.rule_name], 'action')}-${index}`,
      rule_name: first_value([action?.rule_name], 'Compliance rule'),
      outcome: first_value([action?.outcome], ''),
      action_count: Array.isArray(action?.actions) ? action.actions.length : 0,
      actions: Array.isArray(action?.actions) ? action.actions : []
    };
    return normalized_action;
  });
}

function get_compliance_rules(compliance = {}) {
  const rules = (Array.isArray(compliance?.rules) ? compliance.rules : []).map(normalize_rule);
  return rules;
}

function get_compliance_tags(compliance = {}) {
  return Array.isArray(compliance?.entity_tags) ? compliance.entity_tags : [];
}

function get_evaluated_count(evaluation_data = {}, rules = []) {
  const evaluated_count = Number(first_value([
    evaluation_data?.compliance_rules_evaluated,
    rules.filter((rule) => rule.has_evaluation).length
  ], 0));
  return evaluated_count;
}

function get_compliance_message(evaluation_data = {}, rules = []) {
  const message = first_value([
    evaluation_data?.message,
    rules.length ? '' : 'No compliance rules are bound to this onboarding configuration.'
  ], '');
  return message;
}

export function build_compliance_panel_state(case_item = {}, evaluation_result = null) {
  const compliance = case_item?.onboarding_status?.compliance || {};
  const rules = get_compliance_rules(compliance);
  const evaluation_data = evaluation_result?.data || evaluation_result || {};
  const status_label = resolve_panel_status_label(compliance, evaluation_data, rules);
  const panel_state = {
    status_label,
    tone: tone_from_outcome(status_label),
    rules,
    tags: get_compliance_tags(compliance),
    actions_taken: normalize_actions(evaluation_data?.actions_taken),
    evaluated_count: get_evaluated_count(evaluation_data, rules),
    message: get_compliance_message(evaluation_data, rules)
  };
  return panel_state;
}

function resolve_panel_status_label(compliance = {}, evaluation_data = {}, rules = []) {
  const status_label = first_value([
    evaluation_data?.classification,
    compliance?.status,
    compliance?.result,
    compliance?.overall_status,
    build_status_label(rules)
  ], 'Not configured');
  return status_label;
}

export function build_compliance_evaluation_payload(case_item = {}) {
  const payload = {
    entity_type_id: Number(first_value([
      case_item?.threshold_entity_type_id_used,
      case_item?.entity_type_id,
      case_item?.profile?.entity_type_id,
      case_item?.profile?.entity_type?.id
    ], 0)),
    entity_profile_id: Number(first_value([
      case_item?.entity_profile_id,
      case_item?.profile?.id,
      case_item?.profile?.entity_profile_id
    ], 0)),
    application_id: Number(first_value([case_item?.application_id], 0))
  };
  const sub_entity_type_id = Number(first_value([case_item?.sub_entity_type_id], 0));
  const project_id = Number(first_value([case_item?.project_config_binding_id ? case_item?.project_id : 0], 0));
  if (sub_entity_type_id > 0) payload.sub_entity_type_id = sub_entity_type_id;
  if (project_id > 0) payload.project_id = project_id;
  return payload;
}

export async function evaluate_case_compliance(case_item = {}) {
  const payload = build_compliance_evaluation_payload(case_item);
  if (!payload.entity_type_id || !payload.entity_profile_id || !payload.application_id) {
    throw new Error('This onboarding is missing compliance evaluation context.');
  }
  return evaluate_onboarding_execution_compliance(payload);
}
